"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { shown } from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type {
  NEWTONS_METHOD_CALCULATOR_STRINGS,
  RootMethodName,
  RootView,
} from "@/components/tools/newtons-method-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { autoWindow } from "@/lib/tool-kit/calculus/plot";
import {
  bisection,
  type ConvergenceNotes,
  convergenceNotes,
  findBracket,
  newton,
  type StopReason,
  secant,
} from "@/lib/tool-kit/calculus/root-finding";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof NEWTONS_METHOD_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<RootView, { kind: "ok" }>;

const METHOD_ORDER: readonly RootMethodName[] = [
  "newton",
  "secant",
  "bisection",
];
const DRAWN_STEPS = 6;

function digits(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0 ? fmt(0) : fmt(value, { maximumSignificantDigits: 10 });
}

function small(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return value === 0
    ? fmt(0)
    : fmt(value, { notation: "scientific", maximumSignificantDigits: 3 });
}

interface Run {
  method: RootMethodName;
  reason: StopReason;
  root: number;
  residual: number;
  iterates: number[];
  rows: string[][];
  head: string[];
  steps: number;
  /** Segments to draw: [x1, y1, x2, y2] in data units. */
  segments: [number, number, number, number][];
  marks: number[];
}

function runNewton(view: OkView, strings: Strings, fmt: Fmt): Run {
  const run = newton(view.f, view.df, view.start, {
    tolerance: view.tolerance,
    maxIterations: view.maxIterations,
  });
  return {
    method: "newton",
    reason: run.reason,
    root: run.root,
    residual: run.residual,
    iterates: [view.start, ...run.rows.map((r) => r.next)],
    head: [
      strings.columnN,
      strings.columnX,
      strings.columnFx,
      strings.columnDfx,
      strings.columnNext,
      strings.columnStep,
    ],
    rows: run.rows.map((r) => [
      String(r.n),
      digits(fmt, r.x),
      digits(fmt, r.fx),
      digits(fmt, r.dfx),
      digits(fmt, r.next),
      small(fmt, Math.abs(r.step)),
    ]),
    steps: run.rows.length,
    segments: run.rows
      .slice(0, DRAWN_STEPS)
      .filter((r) => Number.isFinite(r.next))
      .map((r) => [r.x, r.fx, r.next, 0]),
    marks: run.rows.map((r) => r.x),
  };
}

function runSecant(view: OkView, strings: Strings, fmt: Fmt): Run {
  const run = secant(view.f, view.start, view.second, {
    tolerance: view.tolerance,
    maxIterations: view.maxIterations,
  });
  return {
    method: "secant",
    reason: run.reason,
    root: run.root,
    residual: run.residual,
    iterates: [view.start, view.second, ...run.rows.map((r) => r.next)],
    head: [
      strings.columnN,
      strings.columnPrevious,
      strings.columnX,
      strings.columnFx,
      strings.columnNext,
      strings.columnStep,
    ],
    rows: run.rows.map((r) => [
      String(r.n),
      digits(fmt, r.previous),
      digits(fmt, r.x),
      digits(fmt, r.fx),
      digits(fmt, r.next),
      small(fmt, Math.abs(r.step)),
    ]),
    steps: run.rows.length,
    segments: run.rows
      .slice(0, DRAWN_STEPS)
      .filter((r) => Number.isFinite(r.next))
      .map((r) => [r.previous, r.fPrevious, r.next, 0]),
    marks: run.rows.map((r) => r.x),
  };
}

function bracketFor(view: OkView): [number, number] | null {
  if (view.left !== null && view.right !== null) {
    return [view.left, view.right];
  }
  return findBracket(view.f, view.start, 10);
}

function runBisection(view: OkView, strings: Strings, fmt: Fmt): Run {
  const bracket = bracketFor(view) ?? [Number.NaN, Number.NaN];
  const run = bisection(view.f, bracket[0], bracket[1], {
    tolerance: view.tolerance,
    maxIterations: Math.max(view.maxIterations, 100),
  });
  return {
    method: "bisection",
    reason: run.reason,
    root: run.root,
    residual: run.residual,
    iterates: run.rows.map((r) => r.mid),
    head: [
      strings.columnN,
      strings.columnA,
      strings.columnB,
      strings.columnMid,
      strings.columnFMid,
      strings.columnWidth,
    ],
    rows: run.rows.map((r) => [
      String(r.n),
      digits(fmt, r.a),
      digits(fmt, r.b),
      digits(fmt, r.mid),
      digits(fmt, r.fMid),
      small(fmt, r.width),
    ]),
    steps: run.rows.length,
    segments: run.rows
      .slice(0, DRAWN_STEPS)
      .map((r) => [r.mid, 0, r.mid, r.fMid]),
    marks: run.rows.map((r) => r.mid),
  };
}

function runFor(
  method: RootMethodName,
  view: OkView,
  strings: Strings,
  fmt: Fmt
): Run {
  if (method === "newton") {
    return runNewton(view, strings, fmt);
  }
  return method === "secant"
    ? runSecant(view, strings, fmt)
    : runBisection(view, strings, fmt);
}

function Notes({
  notes,
  method,
  strings,
  fmt,
}: {
  notes: ConvergenceNotes;
  method: RootMethodName;
  strings: Strings;
  fmt: Fmt;
}) {
  return (
    <div className="space-y-1 text-muted-foreground text-sm">
      {method === "bisection" ? <p>{strings.bisectionNote}</p> : null}
      {method !== "bisection" && notes.order !== null ? (
        <p>
          {t(strings.orderNote, {
            order: fmt(notes.order, { maximumFractionDigits: 2 }),
          })}
        </p>
      ) : null}
      {notes.multiplicity !== null && notes.ratio !== null ? (
        <>
          <p>
            {t(strings.ratioNote, {
              ratio: fmt(notes.ratio, { maximumFractionDigits: 2 }),
            })}
          </p>
          <p>{t(strings.multiplicityNote, { m: notes.multiplicity })}</p>
        </>
      ) : null}
    </div>
  );
}

function Lines({ run, scales }: { run: Run; scales: PlaneScales }) {
  return (
    <>
      {run.segments.map(([x1, y1, x2, y2], index) => (
        <line
          key={`${x1}-${y1}-${x2}-${y2}`}
          opacity={1 - index * 0.1}
          stroke={CHART_COLORS[3]}
          strokeDasharray={run.method === "bisection" ? "3 3" : undefined}
          strokeWidth={1.5}
          x1={scales.x(x1)}
          x2={scales.x(x2)}
          y1={scales.y(y1)}
          y2={scales.y(y2)}
        />
      ))}
    </>
  );
}

export function RootResults({
  view,
  strings,
  fmt,
}: {
  view: OkView;
  strings: Strings;
  fmt: Fmt;
}) {
  const run = useMemo(
    () => runFor(view.method, view, strings, fmt),
    [view, strings, fmt]
  );
  const compare = METHOD_ORDER.map((method) =>
    method === view.method ? run : runFor(method, view, strings, fmt)
  );
  const notes = convergenceNotes(run.iterates);
  const methodName = strings.methods[view.method];
  const reasonNote =
    run.reason === "converged" ? null : strings.reasonNotes[run.reason];
  const window = useMemo(() => {
    const xs = [...run.iterates.slice(0, 10), run.root].filter(
      (x) => Number.isFinite(x) && Math.abs(x) < 1e6
    );
    const lo = Math.min(...xs, view.start);
    const hi = Math.max(...xs, view.start);
    const pad = Math.max((hi - lo) * 0.25, 0.5);
    const base = autoWindow([view.f], lo - pad, hi + pad);
    const ypad = (base.yMax - base.yMin) * 0.05;
    return {
      ...base,
      yMin: Math.min(base.yMin, -ypad),
      yMax: Math.max(base.yMax, ypad),
    };
  }, [run, view.f, view.start]);
  const bracket = bracketFor(view);
  const hasRoot = Number.isFinite(run.root);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.rootLabel}
          value={digits(fmt, run.root)}
        />
        <StatCard
          label={strings.residualLabel}
          value={small(fmt, run.residual)}
        />
        <StatCard label={strings.stepsLabel} value={fmt(run.steps)} />
        <StatCard
          label={strings.reasonLabel}
          value={strings.reasons[run.reason]}
        />
      </StatGrid>
      {reasonNote && run.reason === "max-iterations" ? (
        <Notice>{reasonNote}</Notice>
      ) : null}
      {reasonNote && run.reason !== "max-iterations" ? (
        <ErrorNotice>{reasonNote}</ErrorNotice>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.formulaHeading}</h3>
        <p className="text-foreground text-sm">
          {t(strings.formulas[view.method], {
            derivative: shown(view.derivative, view.decimals),
          })}
        </p>
      </div>
      {run.rows.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
          <MathTable
            head={run.head}
            highlight={
              run.reason === "converged" ? run.rows.length - 1 : undefined
            }
            rows={run.rows}
          />
        </div>
      ) : null}
      {run.reason === "converged" ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.notesHeading}</h3>
          <Notes
            fmt={fmt}
            method={view.method}
            notes={notes}
            strings={strings}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
        <MathTable
          head={[
            strings.columnMethod,
            strings.columnRoot,
            strings.columnResidual,
            strings.columnSteps,
            strings.columnOutcome,
          ]}
          rows={compare.map((c) => [
            strings.methods[c.method],
            digits(fmt, c.root),
            small(fmt, c.residual),
            String(c.steps),
            strings.reasons[c.reason],
          ])}
        />
        <p className="text-muted-foreground text-sm">
          {t(strings.compareNote, {
            second: digits(
              fmt,
              Number.isFinite(view.second) ? view.second : view.start + 0.5
            ),
            a: bracket ? digits(fmt, bracket[0]) : strings.noBracketFound,
            b: bracket ? digits(fmt, bracket[1]) : strings.noBracketFound,
          })}
        </p>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="root-finding-graph"
          over={(scales) => <Lines run={run} scales={scales} />}
          points={
            hasRoot
              ? [
                  ...run.marks
                    .slice(0, 10)
                    .filter((x) => Number.isFinite(x))
                    .map((x) => ({ x, y: 0, color: CHART_COLORS[3] })),
                  {
                    x: run.root,
                    y: 0,
                    color: CHART_COLORS[7],
                    label: t(strings.rootLabelText, {
                      x: fmt(run.root, { maximumSignificantDigits: 6 }),
                    }),
                  },
                ]
              : []
          }
          series={[
            {
              f: view.f,
              color: CHART_COLORS[0],
              label: strings.seriesFunction,
            },
          ]}
          title={t(strings.graphTitle, { method: methodName })}
          window={window}
        />
      </div>
    </>
  );
}
